import React from "react"
import { DesignEditorContext } from "~/contexts/DesignEditor"
import { useEditor, useFrame } from "@agenteresolve/react"
import { DndContext, closestCenter, PointerSensor, useSensor, DragOverlay } from "@dnd-kit/core"
import { arrayMove, SortableContext, horizontalListSortingStrategy } from "@dnd-kit/sortable"
import { restrictToFirstScrollableAncestor, restrictToHorizontalAxis } from "@dnd-kit/modifiers"
import { TimerContext } from "@layerhub-io/use-timer"
import TimelineItem from "./TimelineItem"
import { Block } from "baseui/block"
import { IScene } from "@agenteresolve/types"
import { findSceneIndexByTime } from "~/views/DesignEditor/utils/scenes"
import { renderScenePreview } from "~/utils/previews"

export default function () {
  const { currentScene, scenes, currentPreview, setCurrentPreview, setScenes } = React.useContext(DesignEditorContext)
  const { time } = React.useContext(TimerContext) as any
  const editor = useEditor()
  const frame = useFrame()
  const [draggedScene, setDraggedScene] = React.useState<IScene | null>(null)

  // Highlight the scene under the playhead (also while the animation preview
  // plays, where the edited scene does not change).
  const activeSceneId = React.useMemo(() => {
    if (!scenes || scenes.length === 0) return currentScene?.id
    const index = findSceneIndexByTime(scenes, time || 0)
    if (index >= 0 && scenes[index]) return scenes[index].id
    return currentScene?.id
  }, [scenes, time, currentScene])

  const sensors = [
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  ]

  const makeResizeTimelineItem = React.useCallback(
    (id: string, props: any) => {
      setScenes((prev) =>
        prev.map((scene) => {
          if (scene.id === id) {
            return {
              ...scene,
              duration: props.width * 40,
            }
          }
          return scene
        })
      )
    },
    [setScenes]
  )

  function handleDragStart(event: any) {
    const draggedScene = scenes.find((s) => s.id === event.active.id)
    if (draggedScene) {
      setDraggedScene(draggedScene)
    }
  }

  function handleDragEnd(event: any) {
    const { active, over } = event

    if (active.id !== over.id) {
      setScenes((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id)
        const newIndex = items.findIndex((item) => item.id === over.id)

        return arrayMove(items, oldIndex, newIndex)
      })
    }
    setDraggedScene(null)
  }

  React.useEffect(() => {
    let watcher = async () => {
      const updatedScene = editor.scene.exportToJSON()
      const updatedPreview = await renderScenePreview(editor, updatedScene)
      // Functional update: a stale `scenes` closure must never resurrect an
      // old array (it would wipe previews generated meanwhile).
      setScenes((prev) =>
        prev.map((scene) => {
          if (scene.id === updatedScene.id) {
            return {
              ...updatedScene,
              preview: updatedPreview,
              duration: scene.duration,
            }
          }
          return scene
        })
      )
      setCurrentPreview(updatedPreview)
    }
    if (editor) {
      editor.on("history:changed", watcher)
    }
    return () => {
      if (editor) {
        editor.off("history:changed", watcher)
      }
    }
  }, [editor, setScenes, setCurrentPreview])

  return (
    <DndContext
      modifiers={[restrictToFirstScrollableAncestor, restrictToHorizontalAxis]}
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
    >
      <div style={{ display: "flex" }}>
        <SortableContext items={scenes} strategy={horizontalListSortingStrategy}>
          {scenes.map((page) => (
            <TimelineItem
              key={page.id}
              item={page}
              makeResizeTimelineItem={makeResizeTimelineItem}
              width={page.duration ? page.duration / 40 : 5000 / 40}
              duration={page.duration ? page.duration : 5000}
              height={70}
              frame={frame}
              id={page.id}
              preview={currentPreview && page.id === currentScene?.id ? currentPreview : page.preview || ""}
              isCurrentScene={activeSceneId === page.id}
            />
          ))}
        </SortableContext>
        <DragOverlay>
          {draggedScene ? (
            <Block
              $style={{
                backgroundImage: `url(${draggedScene.preview})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                height: "70px",
                opacity: 0.5,
              }}
            ></Block>
          ) : null}
        </DragOverlay>
      </div>
    </DndContext>
  )
}

// 125px => 5s
// 25px => 1s

// markerRefPosition.y * 40

// px * 40 === time

// 125 * 40 === 5000
